/*
 * File: PatientHomeSpeedDial.js
 * Project: mp-mobile-app
 *
 * Created by Jennifer Loecker on 09/05/2019
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Handles showing the correct form depending on which speed dial option is clicked
 */
import React, { useContext, useState } from "react";
import UIFormDialog from "components/ui/UIFormDialog";
import UISpeedDial from "components/ui/UISpeedDial";
import AssignmentTurnedIn from "@material-ui/icons/AssignmentTurnedIn";
import PersonIcon from "@material-ui/icons/Person";
import DateRangeIcon from "@material-ui/icons/DateRange";
import FavoriteBorderIcon from "@material-ui/icons/FavoriteBorder";
import PatientStore from "services/patients/store/PatientStore";
import PropTypes from "prop-types";
import { useNewApptModel } from "../../appointments/useNewApptModel";
import ApptForm from "services/appointments/components/ApptForm";
import PatientCreatePage from "services/patients/demographics/patient_info/components/PatientCreatePage";
import NewVitals from "../../clinical/vitals/components/NewVitals";
import OrderForm from "services/orders/components/OrderForm";
import OrderModel from "../../../orders/store/models/Order";
import { StoreContext, useStore } from "utils/useStore";
import ProviderOfficeFeeModel from "services/patients/demographics/provider_office_fee/store/ProviderOfficeFeeModel";

const PatientHomeSpeedDial = ({ history, navigationMethod }) => {
  const [showVitalsForm, setShowVitalsForm] = useState(false);
  const [showOrdersForm, setShowOrdersForm] = useState(false);
  const [showApptForm, setShowApptForm] = useState(false);
  const [showPatientForm, setShowPatientForm] = useState(false);

  const storeManager = useContext(StoreContext);
  const patientStore = useStore(PatientStore);
  const providerOfficeStore = useStore(ProviderOfficeFeeModel);
  const patient = patientStore.patientModel;
  const newApptModel = useNewApptModel();

  const toggleVitalsForm = () => {
    setShowVitalsForm(!showVitalsForm);
  };

  const toggleOrdersForm = () => {
    setShowOrdersForm(!showOrdersForm);
  };

  const toggleApptForm = () => {
    setShowApptForm(!showApptForm);
  };

  const togglePatientForm = () => {
    setShowPatientForm(!showPatientForm);
  };

  const speedDialActions = () => {
    return [
      {
        icon: <FavoriteBorderIcon />,
        name: "New Vitals",
        handleClick: toggleVitalsForm
      },
      {
        icon: <AssignmentTurnedIn />,
        name: "New Order",
        handleClick: toggleOrdersForm
      },
      {
        icon: <DateRangeIcon />,
        name: "New Appointment",
        handleClick: toggleApptForm
      },
      {
        icon: <PersonIcon />,
        name: "New Patient",
        handleClick: togglePatientForm
      }
    ];
  };

  const apptForm = () => {
    return <ApptForm parentCallBack={apptFormCallBack} appt={newApptModel} />;
  };

  const apptFormCallBack = newAppointmentId => {
    toggleApptForm();
    const newURL = "/appointments/" + newAppointmentId;
    history.push(newURL);
  };

  const createPatientForm = () => {
    return (
      <PatientCreatePage
        parentCallBack={navigationMethod}
        patient={patientStore.newPatient}
        toggleFormDialog={togglePatientForm}
        createNewAccount={false}
      />
    );
  };

  const createVitals = () => {
    return (
      <NewVitals
        parentCallBack={vitalsFormCallBack}
        patient={patient}
        toggleDialog={toggleVitalsForm}
      />
    );
  };

  const vitalsFormCallBack = newVitalsObject => {
    const patientId = patient.patientId;
    const vitalsId = newVitalsObject.vitalsId;
    const newURL = "/patients/" + patientId + "/clinical/vitals/" + vitalsId;
    history.push(newURL);
  };

  const createOrder = () => {
    let newOrder = OrderModel.create(undefined, storeManager.newEnv());
    newOrder.populateExistingPatient(storeManager, providerOfficeStore);
    return <OrderForm parentCallBack={toggleOrdersForm} order={newOrder} />;
  };

  return (
    <React.Fragment>
      <UISpeedDial actions={speedDialActions()} />
      <UIFormDialog
        open={showPatientForm}
        toggleDialog={togglePatientForm}
        title={"Create Patient"}
        viewPage={createPatientForm}
      />
      <UIFormDialog
        open={showVitalsForm}
        toggleDialog={toggleVitalsForm}
        title={"Create Vitals"}
        viewPage={createVitals}
      />
      <UIFormDialog
        open={showOrdersForm}
        toggleDialog={toggleOrdersForm}
        title={"New Order"}
        viewPage={createOrder}
      />
      <UIFormDialog
        open={showApptForm}
        toggleDialog={toggleApptForm}
        title={"New Appointment"}
        viewPage={apptForm}
      />
    </React.Fragment>
  );
};

PatientHomeSpeedDial.propTypes = {
  navigationMethod: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired
};

export default PatientHomeSpeedDial;
